import type { LangMap } from "./index";

export const video: LangMap = {
  it: {
    tab: "Video",
    slidesTab: "Slide",
    title: "Crea Video di Studio",
    desc: "Trasforma qualsiasi argomento o materiale in un video didattico spiegato da una voce AI, con immagini sincronizzate.",
    topic: "Argomento",
    topicPlaceholder: "Es. Il mito della caverna, Il sistema solare, Equazioni di secondo grado…",
    subject: "Materia (opzionale)",
    subjectPlaceholder: "Filosofia",
    language: "Lingua del video",
    myLanguage: "La mia lingua",
    kind: "Che tipo di video vuoi?",
    audience: "Per chi",
    difficulty: "Difficoltà",
    duration: "Durata",
    minutes: "{{count}} minuti",
    voice: "Voce",
    tone: "Tono",
    character: "Personaggio",
    styles: {
      illustrated: { label: "Illustrato", desc: "Illustrazioni che spiegano i concetti" },
      map: { label: "Con mappa", desc: "La mappa si costruisce mentre la voce spiega" },
      timeline: { label: "Timeline", desc: "Eventi in ordine cronologico" },
      whiteboard: { label: "Lavagna", desc: "Formule e passaggi scritti uno alla volta" },
      storytelling: { label: "Storytelling", desc: "Raccontamelo come una storia" },
      visual: { label: "Visuale", desc: "Parole chiave, poche info per schermata" },
    },
    audiences: { kids: "Per bambini", teens: "Per ragazzi", highschool: "Scuola superiore", university: "Universitario", quickrecap: "Ripasso veloce", deep: "Spiegazione completa" },
    difficulties: { very_simple: "Molto semplice", simple: "Semplice", normal: "Normale", deep: "Approfondito", advanced: "Avanzato" },
    tones: { calm: "Calmo", energetic: "Energico", neutral: "Neutro", narrative: "Narrativo" },
    dsaToggle: "Modalità DSA (meno testo, ritmo più lento)",
    interactiveToggle: "Video interattivo (domande durante il video)",
    quizToggle: "Mini quiz finale",
    tabText: "Testo / file",
    tabApp: "Materiale nell'app",
    sourcePlaceholder: "Incolla appunti, trascrizione della lezione, testo del libro… oppure allega audio/foto qui sotto.",
    reuse: "Riusa quello che hai già creato: non serve ricaricare nulla.",
    myMaps: "Le mie mappe",
    myPodcasts: "I miei podcast",
    myNotes: "I miei appunti",
    linked: "Collegato: {{name}}",
    create: "Crea il video",
    creating: "Sto girando il video…",
    translate: "Traduci",
    translating: "Traduco il video…",
    myVideos: "I miei video",
    emptyLibrary: "Non hai ancora creato video di studio.",
    checkUnderstanding: "Verifica se hai capito",
    check: "Correggi",
    result: "Risultato: {{score}}/{{total}}",
    makeMaterial: "Crea materiale da questo video",
    map: "Mappa",
    flashcards: "Flashcard",
    quiz: "Quiz",
    askTutor: "Chiedi al tutor",
    toasts: {
      ready: "Video pronto ✨",
      translated: "Versione tradotta pronta",
      linked: "Materiale collegato",
      materialCreated: "Materiale creato: lo trovi nella sezione dedicata",
      error: "Si è verificato un errore",
    },
  },
  en: {
    tab: "Video",
    slidesTab: "Slides",
    title: "Create a Study Video",
    desc: "Turn any topic or material into a lesson video narrated by an AI voice, with synced visuals.",
    topic: "Topic",
    topicPlaceholder: "E.g. Plato's cave, The solar system, Quadratic equations…",
    subject: "Subject (optional)",
    subjectPlaceholder: "Philosophy",
    language: "Video language",
    myLanguage: "My language",
    kind: "What kind of video do you want?",
    audience: "Made for",
    difficulty: "Difficulty",
    duration: "Length",
    minutes: "{{count}} minutes",
    voice: "Voice",
    tone: "Tone",
    character: "Character",
    styles: {
      illustrated: { label: "Illustrated", desc: "Illustrations that explain the concepts" },
      map: { label: "With a map", desc: "The map builds while the voice explains" },
      timeline: { label: "Timeline", desc: "Events in chronological order" },
      whiteboard: { label: "Whiteboard", desc: "Formulas and steps written one by one" },
      storytelling: { label: "Storytelling", desc: "Tell it to me like a story" },
      visual: { label: "Visual", desc: "Keywords, little text per screen" },
    },
    audiences: { kids: "For kids", teens: "For teens", highschool: "High school", university: "University", quickrecap: "Quick recap", deep: "Full explanation" },
    difficulties: { very_simple: "Very simple", simple: "Simple", normal: "Normal", deep: "In depth", advanced: "Advanced" },
    tones: { calm: "Calm", energetic: "Energetic", neutral: "Neutral", narrative: "Narrative" },
    dsaToggle: "Accessible mode (less text, slower pace)",
    interactiveToggle: "Interactive video (questions during playback)",
    quizToggle: "Final mini quiz",
    tabText: "Text / file",
    tabApp: "Material in the app",
    sourcePlaceholder: "Paste notes, a lecture transcript, book text… or attach audio/photos below.",
    reuse: "Reuse what you already made: no need to upload anything again.",
    myMaps: "My maps",
    myPodcasts: "My podcasts",
    myNotes: "My notes",
    linked: "Linked: {{name}}",
    create: "Create the video",
    creating: "Shooting the video…",
    translate: "Translate",
    translating: "Translating the video…",
    myVideos: "My videos",
    emptyLibrary: "You haven't created any study videos yet.",
    checkUnderstanding: "Check what you understood",
    check: "Check answers",
    result: "Score: {{score}}/{{total}}",
    makeMaterial: "Create material from this video",
    map: "Map",
    flashcards: "Flashcards",
    quiz: "Quiz",
    askTutor: "Ask the tutor",
    toasts: {
      ready: "Video ready ✨",
      translated: "Translated version ready",
      linked: "Material linked",
      materialCreated: "Material created: find it in its own section",
      error: "Something went wrong",
    },
  },
  es: {
    tab: "Vídeo",
    slidesTab: "Diapositivas",
    title: "Crear vídeo de estudio",
    desc: "Convierte cualquier tema o material en un vídeo didáctico narrado por una voz IA, con imágenes sincronizadas.",
    topic: "Tema",
    topicPlaceholder: "Ej. El mito de la caverna, El sistema solar, Ecuaciones de segundo grado…",
    subject: "Asignatura (opcional)",
    subjectPlaceholder: "Filosofía",
    language: "Idioma del vídeo",
    myLanguage: "Mi idioma",
    kind: "¿Qué tipo de vídeo quieres?",
    audience: "Para quién",
    difficulty: "Dificultad",
    duration: "Duración",
    minutes: "{{count}} minutos",
    voice: "Voz",
    tone: "Tono",
    character: "Personaje",
    styles: {
      illustrated: { label: "Ilustrado", desc: "Ilustraciones que explican los conceptos" },
      map: { label: "Con mapa", desc: "El mapa se construye mientras la voz explica" },
      timeline: { label: "Cronología", desc: "Eventos en orden cronológico" },
      whiteboard: { label: "Pizarra", desc: "Fórmulas y pasos escritos uno a uno" },
      storytelling: { label: "Narrativo", desc: "Cuéntamelo como una historia" },
      visual: { label: "Visual", desc: "Palabras clave, poca info por pantalla" },
    },
    audiences: { kids: "Para niños", teens: "Para adolescentes", highschool: "Bachillerato", university: "Universidad", quickrecap: "Repaso rápido", deep: "Explicación completa" },
    difficulties: { very_simple: "Muy simple", simple: "Simple", normal: "Normal", deep: "Profundo", advanced: "Avanzado" },
    tones: { calm: "Calmado", energetic: "Enérgico", neutral: "Neutro", narrative: "Narrativo" },
    dsaToggle: "Modo accesible (menos texto, ritmo más lento)",
    interactiveToggle: "Vídeo interactivo (preguntas durante el vídeo)",
    quizToggle: "Mini test final",
    tabText: "Texto / archivo",
    tabApp: "Material en la app",
    sourcePlaceholder: "Pega apuntes, la transcripción de la clase, texto del libro… o adjunta audio/fotos abajo.",
    reuse: "Reutiliza lo que ya has creado: no hace falta subir nada.",
    myMaps: "Mis mapas",
    myPodcasts: "Mis podcasts",
    myNotes: "Mis apuntes",
    linked: "Vinculado: {{name}}",
    create: "Crear el vídeo",
    creating: "Grabando el vídeo…",
    translate: "Traducir",
    translating: "Traduciendo el vídeo…",
    myVideos: "Mis vídeos",
    emptyLibrary: "Aún no has creado vídeos de estudio.",
    checkUnderstanding: "Comprueba si lo has entendido",
    check: "Corregir",
    result: "Resultado: {{score}}/{{total}}",
    makeMaterial: "Crear material a partir de este vídeo",
    map: "Mapa",
    flashcards: "Fichas",
    quiz: "Test",
    askTutor: "Pregunta al tutor",
    toasts: {
      ready: "Vídeo listo ✨",
      translated: "Versión traducida lista",
      linked: "Material vinculado",
      materialCreated: "Material creado: lo encuentras en su sección",
      error: "Ha ocurrido un error",
    },
  },
  fr: {
    tab: "Vidéo",
    slidesTab: "Diapos",
    title: "Créer une vidéo d'étude",
    desc: "Transforme n'importe quel sujet ou support en vidéo pédagogique racontée par une voix IA, avec des visuels synchronisés.",
    topic: "Sujet",
    topicPlaceholder: "Ex. L'allégorie de la caverne, Le système solaire, Équations du second degré…",
    subject: "Matière (facultatif)",
    subjectPlaceholder: "Philosophie",
    language: "Langue de la vidéo",
    myLanguage: "Ma langue",
    kind: "Quel type de vidéo veux-tu ?",
    audience: "Pour qui",
    difficulty: "Difficulté",
    duration: "Durée",
    minutes: "{{count}} minutes",
    voice: "Voix",
    tone: "Ton",
    character: "Personnage",
    styles: {
      illustrated: { label: "Illustrée", desc: "Des illustrations qui expliquent les concepts" },
      map: { label: "Avec carte", desc: "La carte se construit pendant l'explication" },
      timeline: { label: "Frise", desc: "Les événements en ordre chronologique" },
      whiteboard: { label: "Tableau", desc: "Formules et étapes écrites une à une" },
      storytelling: { label: "Récit", desc: "Raconte-le comme une histoire" },
      visual: { label: "Visuelle", desc: "Mots-clés, peu d'infos par écran" },
    },
    audiences: { kids: "Pour les enfants", teens: "Pour les ados", highschool: "Lycée", university: "Université", quickrecap: "Révision express", deep: "Explication complète" },
    difficulties: { very_simple: "Très simple", simple: "Simple", normal: "Normal", deep: "Approfondi", advanced: "Avancé" },
    tones: { calm: "Calme", energetic: "Énergique", neutral: "Neutre", narrative: "Narratif" },
    dsaToggle: "Mode accessible (moins de texte, rythme plus lent)",
    interactiveToggle: "Vidéo interactive (questions pendant la vidéo)",
    quizToggle: "Mini quiz final",
    tabText: "Texte / fichier",
    tabApp: "Contenus de l'app",
    sourcePlaceholder: "Colle des notes, la transcription du cours, un texte du manuel… ou joins un audio/une photo ci-dessous.",
    reuse: "Réutilise ce que tu as déjà créé : inutile de tout réimporter.",
    myMaps: "Mes cartes",
    myPodcasts: "Mes podcasts",
    myNotes: "Mes notes",
    linked: "Lié : {{name}}",
    create: "Créer la vidéo",
    creating: "Je tourne la vidéo…",
    translate: "Traduire",
    translating: "Je traduis la vidéo…",
    myVideos: "Mes vidéos",
    emptyLibrary: "Tu n'as pas encore créé de vidéo d'étude.",
    checkUnderstanding: "Vérifie ce que tu as compris",
    check: "Corriger",
    result: "Résultat : {{score}}/{{total}}",
    makeMaterial: "Créer du matériel à partir de cette vidéo",
    map: "Carte",
    flashcards: "Flashcards",
    quiz: "Quiz",
    askTutor: "Demander au tuteur",
    toasts: {
      ready: "Vidéo prête ✨",
      translated: "Version traduite prête",
      linked: "Contenu lié",
      materialCreated: "Contenu créé : tu le trouves dans sa section",
      error: "Une erreur est survenue",
    },
  },
  de: {
    tab: "Video",
    slidesTab: "Folien",
    title: "Lernvideo erstellen",
    desc: "Verwandle jedes Thema oder Material in ein Lernvideo mit KI-Stimme und synchronen Bildern.",
    topic: "Thema",
    topicPlaceholder: "z. B. Höhlengleichnis, Sonnensystem, Quadratische Gleichungen…",
    subject: "Fach (optional)",
    subjectPlaceholder: "Philosophie",
    language: "Sprache des Videos",
    myLanguage: "Meine Sprache",
    kind: "Welche Art Video willst du?",
    audience: "Für wen",
    difficulty: "Schwierigkeit",
    duration: "Länge",
    minutes: "{{count}} Minuten",
    voice: "Stimme",
    tone: "Ton",
    character: "Figur",
    styles: {
      illustrated: { label: "Illustriert", desc: "Illustrationen erklären die Konzepte" },
      map: { label: "Mit Mindmap", desc: "Die Map entsteht, während die Stimme erklärt" },
      timeline: { label: "Zeitstrahl", desc: "Ereignisse in chronologischer Reihenfolge" },
      whiteboard: { label: "Tafel", desc: "Formeln und Schritte nacheinander geschrieben" },
      storytelling: { label: "Erzählung", desc: "Erzähl es mir wie eine Geschichte" },
      visual: { label: "Visuell", desc: "Schlüsselwörter, wenig Text pro Bild" },
    },
    audiences: { kids: "Für Kinder", teens: "Für Jugendliche", highschool: "Oberstufe", university: "Universität", quickrecap: "Schnelle Wiederholung", deep: "Vollständige Erklärung" },
    difficulties: { very_simple: "Sehr einfach", simple: "Einfach", normal: "Normal", deep: "Vertieft", advanced: "Fortgeschritten" },
    tones: { calm: "Ruhig", energetic: "Energisch", neutral: "Neutral", narrative: "Erzählend" },
    dsaToggle: "Barrierearmer Modus (weniger Text, langsamer)",
    interactiveToggle: "Interaktives Video (Fragen während des Videos)",
    quizToggle: "Mini-Quiz am Ende",
    tabText: "Text / Datei",
    tabApp: "Material in der App",
    sourcePlaceholder: "Füge Notizen, ein Unterrichtstranskript oder Buchtext ein … oder hänge unten Audio/Fotos an.",
    reuse: "Nutze, was du schon erstellt hast: nichts erneut hochladen.",
    myMaps: "Meine Maps",
    myPodcasts: "Meine Podcasts",
    myNotes: "Meine Notizen",
    linked: "Verknüpft: {{name}}",
    create: "Video erstellen",
    creating: "Video wird gedreht…",
    translate: "Übersetzen",
    translating: "Video wird übersetzt…",
    myVideos: "Meine Videos",
    emptyLibrary: "Du hast noch keine Lernvideos erstellt.",
    checkUnderstanding: "Prüfe, ob du es verstanden hast",
    check: "Auswerten",
    result: "Ergebnis: {{score}}/{{total}}",
    makeMaterial: "Material aus diesem Video erstellen",
    map: "Map",
    flashcards: "Karteikarten",
    quiz: "Quiz",
    askTutor: "Tutor fragen",
    toasts: {
      ready: "Video fertig ✨",
      translated: "Übersetzte Version fertig",
      linked: "Material verknüpft",
      materialCreated: "Material erstellt: du findest es im passenden Bereich",
      error: "Etwas ist schiefgelaufen",
    },
  },
  pt: {
    tab: "Vídeo",
    slidesTab: "Slides",
    title: "Criar vídeo de estudo",
    desc: "Transforma qualquer tema ou material num vídeo didático narrado por uma voz de IA, com imagens sincronizadas.",
    topic: "Tema",
    topicPlaceholder: "Ex. O mito da caverna, O sistema solar, Equações do 2.º grau…",
    subject: "Disciplina (opcional)",
    subjectPlaceholder: "Filosofia",
    language: "Idioma do vídeo",
    myLanguage: "O meu idioma",
    kind: "Que tipo de vídeo queres?",
    audience: "Para quem",
    difficulty: "Dificuldade",
    duration: "Duração",
    minutes: "{{count}} minutos",
    voice: "Voz",
    tone: "Tom",
    character: "Personagem",
    styles: {
      illustrated: { label: "Ilustrado", desc: "Ilustrações que explicam os conceitos" },
      map: { label: "Com mapa", desc: "O mapa constrói-se enquanto a voz explica" },
      timeline: { label: "Cronologia", desc: "Eventos por ordem cronológica" },
      whiteboard: { label: "Quadro", desc: "Fórmulas e passos escritos um a um" },
      storytelling: { label: "Narrativa", desc: "Conta-me como uma história" },
      visual: { label: "Visual", desc: "Palavras-chave, pouca informação por ecrã" },
    },
    audiences: { kids: "Para crianças", teens: "Para adolescentes", highschool: "Secundário", university: "Universidade", quickrecap: "Revisão rápida", deep: "Explicação completa" },
    difficulties: { very_simple: "Muito simples", simple: "Simples", normal: "Normal", deep: "Aprofundado", advanced: "Avançado" },
    tones: { calm: "Calmo", energetic: "Enérgico", neutral: "Neutro", narrative: "Narrativo" },
    dsaToggle: "Modo acessível (menos texto, ritmo mais lento)",
    interactiveToggle: "Vídeo interativo (perguntas durante o vídeo)",
    quizToggle: "Mini questionário final",
    tabText: "Texto / ficheiro",
    tabApp: "Material na app",
    sourcePlaceholder: "Cola apontamentos, a transcrição da aula, texto do livro… ou anexa áudio/fotos abaixo.",
    reuse: "Reaproveita o que já criaste: não precisas de carregar nada de novo.",
    myMaps: "Os meus mapas",
    myPodcasts: "Os meus podcasts",
    myNotes: "Os meus apontamentos",
    linked: "Ligado: {{name}}",
    create: "Criar o vídeo",
    creating: "A gravar o vídeo…",
    translate: "Traduzir",
    translating: "A traduzir o vídeo…",
    myVideos: "Os meus vídeos",
    emptyLibrary: "Ainda não criaste vídeos de estudo.",
    checkUnderstanding: "Vê se percebeste",
    check: "Corrigir",
    result: "Resultado: {{score}}/{{total}}",
    makeMaterial: "Criar material a partir deste vídeo",
    map: "Mapa",
    flashcards: "Flashcards",
    quiz: "Questionário",
    askTutor: "Perguntar ao tutor",
    toasts: {
      ready: "Vídeo pronto ✨",
      translated: "Versão traduzida pronta",
      linked: "Material ligado",
      materialCreated: "Material criado: encontra-o na secção respetiva",
      error: "Ocorreu um erro",
    },
  },
};
